import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "next/navigation";
import PropTypes from "prop-types";
import BackToTop from "@/components/backToTop";
import Loader from "@/components/loader";
import FilterContent from "./FilterContent";
import NewPLPCard from "@/components/ProductListing/PLPCards/index";
import PLPHeader from "./PLPHeader";
import PLPOrderBySelect from "./PLPOrderBySelect";
import CompareProducts from "@/components/CustomComponents/CompareProducts";
import {
  selectOptions,
  formatterHeader,
  staticLabelsPLP,
  buildFacetObjectFromLabelsAndFields,
  buildFqParams,
} from "@/components/ProductListing/helper";
import { apim } from "@/constants/api";
import {
  APIM_BASE_URL,
  FILTERS_FL,
  lwAppName,
  PLP_END_POINT_CLIENT,
  searchPLPEndPoint,
  SSR_PLP_FL,
} from "@/constants";
import { formatListingData } from "@/services/server/plpHelper";
import { getPlpProcessedData } from "@/services/server/plpResponseProcessor";
import { scroller } from "react-scroll";

const ProductListing = ({
  locale = "en",
  plpData = {},
  facetLabelsState = {},
  categoryConfig = "",
  params: { slug = "" } = {},
  search = false,
  onNoResultsChange = () => {},
  compareMappingsData = {},
}) => {
  const defaultLocale = locale === "default" ? "en" : locale;
  const apiLocale =
    locale === "default" ? "en" : locale === "vn" ? "vi" : locale;

  const searchParams = useSearchParams();
  const {
    facetlabels = {},
    facetFields = {},
    colorSwatchLabels = {},
    facetRanges = {},
    category_labels = {},
  } = facetLabelsState;

  const { data: plpListingData = [], total = 0 } = plpData;

  const filterFacets = Object.keys(facetlabels).length
    ? buildFacetObjectFromLabelsAndFields(
        facetlabels,
        facetFields,
        colorSwatchLabels,
        facetRanges
      )
    : {};

  const handleIfAnyPreFilters = () => {
    const params = new URLSearchParams(searchParams);
    const preFilterObj = Object.fromEntries(params.entries());
    if (!Object.entries(preFilterObj).length)
      return {
        matchedFacetLabels: {},
        preFilterObj: {},
        preselectedSort: "",
        preSelectedFacetCount: {},
      };
    const preselectedSort =
      preFilterObj.orderby || preFilterObj.sort
        ? `${preFilterObj.orderby} ${preFilterObj.sort}`
        : "";
    const matchedFacetLabels = Object.keys(facetlabels)
      .map(key => {
        if (preFilterObj.hasOwnProperty(facetlabels[key])) {
          return { [key]: preFilterObj[facetlabels[key]] };
        }
      })
      .filter(item => item);
    const selectedFacetLabels = {};
    const preSelectedFacetCount = {};
    matchedFacetLabels.forEach((item, index) => {
      const key = Object.keys(item)[0];
      selectedFacetLabels[key] = item[key];
      const countIndex = facetFields[key]?.findIndex(
        countItem => countItem === item[key]
      );
      const count = facetFields[key]?.[countIndex + 1];
      preSelectedFacetCount[facetlabels[key]] = count;
    });

    return {
      matchedFacetLabels: selectedFacetLabels,
      preFilterObj,
      preselectedSort,
      preSelectedFacetCount,
    };
  };

  const {
    matchedFacetLabels = {},
    preFilterObj = {},
    preselectedSort = "",
    preSelectedFacetCount = {},
  } = handleIfAnyPreFilters();

  const hasAnyPrefilters = useRef(
    Object.keys(matchedFacetLabels).length || preselectedSort ? true : false
  );

  const needApiCall = useRef(false);
  const filtersAppliedRef = useRef(false);
  const productsRef = useRef([]);
  const totalProductCountRef = useRef(total);
  const loadMoreRef = useRef(null);

  const [plpStateData, setPlpStateData] = useState(plpListingData);

  const [filterSelectedOnAction, setFilterSelectedOnAction] = useState(
    matchedFacetLabels || {}
  );
  const [isHovering, setIsHovering] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSkuId, setSelectedSkuId] = useState("");
  const [apiRequestBody, setApiRequestBody] = useState(preFilterObj || {});
  const [selectedFilterCount, setSelectedFilterCount] = useState(
    preSelectedFacetCount || {}
  );
  const [totalProductCount, setTotalProductCount] = useState(total);
  const [isLoading, setIsLoading] = useState(false);
  const [mappedFilterFacets, setMappedFilterFacets] = useState(filterFacets);
  const [selectedSortOrder, setSelectedSortOrder] = useState(
    preselectedSort || ""
  );
  const [rows, setRows] = useState(30);
  const [start, setStart] = useState(0);
  const hasMore = useRef(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      scroller.scrollTo("plp-section", {
        duration: 2000,
        smooth: "easeInOutQuart",
      });
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleFilterFromQuery = (filterObjWithAlias = {}, aliasField = "") => {
    const params = new URLSearchParams(searchParams);

    if (aliasField) {
      params.delete(aliasField);
      aliasField === "orderby" && params.delete("sort");
    }

    Object.keys(filterObjWithAlias).forEach(key => {
      const value = filterObjWithAlias[key];
      params.set(key, value);
    });
    const queryString = params.toString();
    const newRelativePathQuery = queryString
      ? `?${queryString}`
      : window.location.pathname;

    window.history.replaceState(null, "", newRelativePathQuery);
  };

  const prepareProductData = async (data = {}) => {
    const {
      fusion: {
        facet_labels: facetlabels = {},
        color_swatch_labels: colorSwatchLabels = {},
      } = {},
      facet_counts: {
        facet_fields: facetFields = {},
        facet_ranges: facetRanges = {},
      } = {},
      response: { numFound = 0 } = {},
    } = data || {};
    const formattedData = await formatListingData(data, false);
    const plpData = await getPlpProcessedData(
      formattedData,
      false,
      [],
      "",
      {},
      {}
    );
    const { data: plpListData = [] } = plpData || {};
    const filterFacets = buildFacetObjectFromLabelsAndFields(
      facetlabels,
      facetFields,
      colorSwatchLabels,
      facetRanges
    );
    setMappedFilterFacets(filterFacets);
    if (needApiCall.current) {
      setPlpStateData(prevData => {
        return [...prevData, ...plpListData];
      });
    } else {
      setPlpStateData(plpListData);
    }
    needApiCall.current = false;
    filtersAppliedRef.current = false;
    totalProductCountRef.current = numFound;
    setTotalProductCount(numFound);
    setIsLoading(false);
  };

  useEffect(() => {
    hasMore.current = productsRef.current.length < totalProductCountRef.current;
    productsRef.current = plpStateData;
  }, [plpStateData]);

  useEffect(() => {
    onNoResultsChange(!isLoading && !plpStateData.length);
  }, [isLoading, plpStateData, onNoResultsChange]);

  const executeSearchPLP = useCallback(() => {
    setIsLoading(true);
    const profileName = `profile_${lwAppName}_Search`;
    const query = `q=${encodeURIComponent(search)}&fq=language_s:${encodeURIComponent(apiLocale)}&profilename=${profileName}&fl=${SSR_PLP_FL.join(",")}&collections=${lwAppName}`;
    const url = `${APIM_BASE_URL}${searchPLPEndPoint}?${query}`;
    apim
      .get(url)
      .then(response => {
        if (response?.data) {
          prepareProductData(response.data);
        }
      })
      .catch(err => {
        setIsLoading(false);
      });
  }, [search, apiLocale]);

  useEffect(() => {
    if (search) {
      executeSearchPLP();
    }
  }, [search, executeSearchPLP]);

  const handleMouseOver = id => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
    setSelectedSkuId("");
  };

  const handleModalOpen = (sku = "") => {
    setModalOpen(true);
    // setIsHovering(false)
    setSelectedSkuId(sku);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleRelavanceSort = (value = "") => {
    const postBody = { ...apiRequestBody };
    delete postBody.orderby;
    delete postBody.sort;
    handleFilterFromQuery(postBody, "orderby");
    setSelectedSortOrder(value);
    needApiCall.current = false;
    filtersAppliedRef.current = true;
  };

  const orderBySelect = async e => {
    const { target: { value = "" } = {} } = e;
    const postBody = { ...apiRequestBody };
    setIsLoading(true);

    if (value === " ") {
      handleRelavanceSort(value);
      return;
    }

    if (value) {
      postBody.orderby = value?.split(" ")[0];
      postBody.sort = value?.split(" ")[1];
      handleFilterFromQuery(postBody);
    } else {
      delete postBody.orderby;
      delete postBody.sort;
      handleFilterFromQuery(postBody, "orderby");
    }
    value
      ? setSelectedSortOrder(`${postBody.orderby} ${postBody.sort}`)
      : setSelectedSortOrder("");
    needApiCall.current = false;
    filtersAppliedRef.current = true;
  };

  const executePLPClientSide = useCallback(
    (fqParams = [], sortQuery = "", start = 0, rows = 30) => {
      if (search && !filtersAppliedRef.current) return;
      const paramsFq = new URLSearchParams();
      fqParams.forEach(fq => paramsFq.append("fq", fq));

      if (sortQuery) paramsFq.append("sort", sortQuery);

      if (!needApiCall.current) {
        setIsLoading(true);
        setPlpStateData([]);
      }
      // return
      const profileName = search
        ? `profile_${lwAppName}_Search`
        : categoryConfig;
      const url = `${PLP_END_POINT_CLIENT}?profilename=${profileName}&collections=${lwAppName}&${paramsFq.toString()}&fl=${SSR_PLP_FL.join(",")}&rows=${rows}&start=${start}`;
      apim
        .get(url)
        .then(async res => {
          const { data = null } = res || {};
          if (data) {
            prepareProductData(data);
          }
        })
        .catch(err => {
          console.log(err, "error in client side plp");
          setIsLoading(false);
        });
    },
    [categoryConfig, search]
  );

  const fetchMoreProducts = () => {
    hasMore.current = productsRef.current.length < totalProductCountRef.current;

    needApiCall.current = true;
    if (hasMore.current) setStart(prevStart => prevStart + rows);
  };

  const shouldFetchProducts =
    hasAnyPrefilters.current ||
    needApiCall.current ||
    filtersAppliedRef.current;

  const fetchProducts = useCallback(() => {
    let rowsVal = 30;
    let startModified = 0;
    const fqParams = buildFqParams(filterSelectedOnAction, defaultLocale);
    let sortQuery = "";
    if (selectedSortOrder) {
      if (selectedSortOrder !== " ") {
        sortQuery = selectedSortOrder;
      }
    }
    if (fqParams.length > 1 || sortQuery) {
      rowsVal = rowsVal + start;
      startModified = start;
    } else {
      startModified = start;
    }
    executePLPClientSide(fqParams, sortQuery, startModified, rowsVal);
  }, [
    filterSelectedOnAction,
    selectedSortOrder,
    start,
    defaultLocale,
    executePLPClientSide,
  ]);

  useEffect(() => {
    const checkAndFetch =
      hasAnyPrefilters.current ||
      needApiCall.current ||
      filtersAppliedRef.current;

    if (checkAndFetch) {
      fetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProducts]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoading && hasMore.current) {
          fetchMoreProducts();
        }
      },
      { rootMargin: "100px" }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [isLoading]);

  const normalize = v => String(v).trim();

  const filterSelect = async (filterRequest = {}, filterCount = {}) => {
    const entries = Object.entries(filterRequest);
    const [field, rawValue] = entries[0];

    const filterObj = { ...filterSelectedOnAction };
    const apiRequestBodyObj = { ...apiRequestBody };
    const selectedFilterCountObj = { ...selectedFilterCount };
    const filterCountEntries = Object.entries(filterCount);
    const [aliasFieldName, countValue] = filterCountEntries[0];
    if (rawValue === "") {
      delete filterObj[field];
      delete apiRequestBodyObj[aliasFieldName];
      delete selectedFilterCountObj[aliasFieldName];
      setFilterSelectedOnAction(filterObj);
      setApiRequestBody(apiRequestBodyObj);
      setSelectedFilterCount(selectedFilterCountObj);
      handleFilterFromQuery(apiRequestBodyObj, aliasFieldName);
    } else {
      filterObj[field] = rawValue;
      setFilterSelectedOnAction(filterObj);

      apiRequestBodyObj[aliasFieldName] = rawValue;
      selectedFilterCountObj[aliasFieldName] = countValue;
      setApiRequestBody(apiRequestBodyObj);
      setSelectedFilterCount(selectedFilterCountObj);
      handleFilterFromQuery(apiRequestBodyObj);
    }
    filtersAppliedRef.current = true;
    needApiCall.current = false;
  };

  return (
    <>
      {isLoading && <Loader loading={isLoading} />}
      <section
        id="plp-section"
        className="max-w-screen-lg mx-auto bg-white text-[#232323] overflow-auto"
      >
        <div className="flex justify-between flex-wrap md:flex-nowrap">
          {!search ? (
            <PLPHeader
              productCount={totalProductCount}
              pageHeading={formatterHeader(
                slug,
                categoryConfig,
                category_labels
              )}
              locale={defaultLocale}
            />
          ) : (
            <div className="flex flex-col pt-[50px] pb-[22px] m-[10px] border-b-2 md:border-b-0 w-full"></div>
          )}
          <PLPOrderBySelect
            orderBySelect={e => orderBySelect(e)}
            sortingArray={selectOptions[defaultLocale]}
            selectedSortOrder={selectedSortOrder}
          />
          {/* order by select -- end  */}
        </div>
        <div className="flex flex-wrap md:flex-nowrap w-full flex-col md:flex-row md:gap-[60px]">
          <FilterContent
            filterOptions={mappedFilterFacets}
            slug={slug}
            filterSelect={filterSelect}
            selectedFilter={apiRequestBody}
            selectedFilterCount={selectedFilterCount}
            locale={defaultLocale}
            totalProductCount={totalProductCount}
            setSelectedFilterCount={setSelectedFilterCount}
            categoryConfig={formatterHeader(
              slug,
              categoryConfig,
              category_labels
            )}
            pageType={search ? "search" : "plp"}
            search={search}
          />
          {/* cards div */}
          <div className="flex content-start items-start md:ml-[30px] mb-[40px] mt-[20px] pb-[20px] md:w-3/4 flex-wrap">
            {/* Repeater div 1 */}
            {plpStateData.length ? (
              plpStateData?.map(item => (
                <NewPLPCard
                  handleMouseOut={() => handleMouseOut()}
                  handleMouseOver={e => handleMouseOver(e)}
                  isHovering={isHovering}
                  selectedSkuId={selectedSkuId}
                  item={item}
                  handleModalOpen={(sku = "") => handleModalOpen(sku)}
                  handleModalClose={() => handleModalClose()}
                  modalOpen={modalOpen}
                  key={item?.sku}
                  locale={defaultLocale}
                  displayCompare={!search}
                  compareMappingsData={compareMappingsData}
                />
              ))
            ) : (
              <div>{staticLabelsPLP[defaultLocale]?.noProductFound}</div>
            )}
          </div>
        </div>
      </section>
      <div ref={loadMoreRef} />
      <BackToTop topHeight={0} localeProp={defaultLocale} />
      <CompareProducts />
    </>
  );
};

ProductListing.propTypes = {
  locale: PropTypes.string.isRequired,
  plpData: PropTypes.object,
  facetLabelsState: PropTypes.object,
  categoryConfig: PropTypes.string,
  onNoResultsChange: PropTypes.func,
  params: PropTypes.shape({
    slug: PropTypes.string,
  }),
};

export default ProductListing;
